import { ConstrainMode, IColumn, IDetailsRowProps, IObjectWithKey } from '@fluentui/react';
import { IPScrollablePane, IPShimmeredDetailsList, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode, IPSelection } from 'baseComponents/Utils/Selection';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { ColumnDefinition } from 'microsoft-graph';
import ResourceLink from 'models/resourceLink';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { graphHasValidDataType } from 'services/Graph/graphServiceList';
import { getLinkNameFromListItem, IListItem } from '../../services/Graph/SharepointInterfaces';
import { onRenderForColumn } from './ColumnRenderers';

interface IMyListItemsProps {
  items: IListItem[];
  columns: ColumnDefinition[];
  loading: boolean;
  onSelect: (items: IListItem[]) => void;
  viewMode?: boolean;
  existingLinks?: ResourceLink[];
  linkToEdit?: ResourceLink;
  singleSelect?: boolean;
}

const convertToRenderColumn = (columns: ColumnDefinition[]): IColumn[] => {
  return columns.map((columnDef: ColumnDefinition) => ({
    key: `${columnDef.name}`,
    name: `${columnDef.displayName}`,
    minWidth: 100,
    isResizable: true,
    isMultiline: true,
    onRender: (_item?: IListItem, index?: number, column?: IColumn) => {
      if (!_item || !columnDef) return;

      return onRenderForColumn(_item, columnDef);
    },
  }));
};

//Filter only supported columns : boolean, dateTime, personOrGroup , text , currency ,number , choice
const filterSupportedColumns = (items: ColumnDefinition[]): ColumnDefinition[] => {
  return items.filter((item) => {
    return graphHasValidDataType(item);
  });
};

const MyListItems = (props: IMyListItemsProps) => {
  const [columns, setColumns] = useState<IColumn[]>([]);
  const { t } = useTranslation(['sharepoint']);

  useEffect(() => {
    const cols = convertToRenderColumn(filterSupportedColumns(props.columns));
    setColumns(cols);
  }, [props.columns, props.items]);

  const selection = new IPSelection<IListItem>({
    canSelectItem(item) {
      const itemName = getLinkNameFromListItem(item);

      return (
        item.id !== undefined &&
        !props.existingLinks?.find(
          (existingLink) =>
            props.linkToEdit?.linkId !== existingLink.linkId &&
            (existingLink.listItemId === item.id || existingLink.linkName.toLowerCase() === itemName.toLowerCase()),
        )
      );
    },
    getKey(item) {
      return item.id || '';
    },
    onSelectionChanged: () => {
      const items = selection.getSelection() as IListItem[];
      props.onSelect(items);
    },
  });

  const onRenderRow = (
    rowProps?: IDetailsRowProps,
    defaultRender?: (rowProps?: IDetailsRowProps) => JSX.Element | null,
  ): JSX.Element | null => {
    if (defaultRender && rowProps?.item && !selection.canSelectItem(rowProps.item) && !rowProps.item.folder) {
      rowProps.disabled = true;

      return (
        <IPTooltipHost
          content={t('sharepoint:SharepointPicker.ItemExists')}
          directionalHint={IPDirectionalHint.topLeftEdge}
        >
          {defaultRender(rowProps)}
        </IPTooltipHost>
      );
    }

    if (defaultRender) {
      return defaultRender(rowProps);
    } else {
      return null;
    }
  };

  //
  // Main render
  //
  if (props.loading) {
    return (
      <IPStack verticalFill verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (props.items.length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('sharepoint:SharepointPicker.NoData')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill>
      <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPShimmeredDetailsList
            columns={columns}
            compact
            layoutMode={IPDetailsListLayoutMode.fixedColumns}
            constrainMode={ConstrainMode.unconstrained}
            items={props.items}
            selectionMode={
              props.viewMode
                ? props.singleSelect
                  ? IPSelectionMode.single
                  : IPSelectionMode.none
                : IPSelectionMode.multiple
            }
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            enableShimmer={props.loading}
            shimmerLines={4}
            selection={selection as IPSelection<IObjectWithKey>}
            onRenderRow={onRenderRow}
          />
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};

export default MyListItems;
